import { restoreToken } from "common/utils/token";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AnalyticsEvents, analytics } from "../../common/analytics";

import { useFormik } from "formik";

import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FullScreenDialog } from "common/components";
import { useTranslation } from "common/locales";
import {
  markDeviceAsRegistered,
  setupPushNotificationsAndRegisterDevice,
  shouldRegisterDevice,
} from "common/utils/deviceRegistration";
import { useCallback, useEffect, useState } from "react";
import {
  createUser,
  fetchTokenAndStore,
  initializeApp,
  initializeFarm,
} from "services";
import { useAppStore } from "stores";
import UserDetails from "./UserDetailsFromEmail";
import Welcome from "./Welcome";

export interface FormValues {
  mail: string;
  password: string;
  confirmPassword: string;
}

export interface FormErrors {
  confirmPassword?: string | undefined;
}

export default function SignupDialog() {
  const navigate = useNavigate();
  const location = useLocation();
  const creatingUser = useAppStore((state) => state.isLoggingIn);
  const appInitialising = useAppStore((state) => state.loading);
  const loading = creatingUser || appInitialising === "pending";
  const params = new URLSearchParams(location.search);

  const storedEmail = window.localStorage.getItem("signupEmail") || "";
  const storedName = window.localStorage.getItem("signupName") || "";
  const storedPhone = window.localStorage.getItem("signupPhone") || "";
  const storedTradingName =
    window.localStorage.getItem("signupTradingName") || "";

  const tryLoadAppWithToken = useCallback(async () => {
    try {
      // A pending programme invitation takes precedence over the regular
      // post-auth routing — otherwise the freshly-signed-up farmer would be
      // sent to /add-farm/enter-name before handleSignup's explicit invite
      // redirect can fire.
      const pendingInviteToken =
        window.localStorage.getItem("pendingInviteToken");
      if (pendingInviteToken) {
        navigate(`/invite/${pendingInviteToken}`);
        return;
      }
      const res = await initializeApp();
      const farms = res?.farms;
      if (farms && farms.length > 0) {
        const defaultFarm = farms[0];
        await initializeFarm(defaultFarm);
        navigate(`/farm/${defaultFarm.guid}`);
      } else {
        navigate("/add-farm/enter-name");
      }
    } catch (e) {
      console.log(e);
    }
  }, [navigate]);

  useEffect(() => {
    // Skip auto-redirect if already on the welcome page (allows dev testing of welcome screen)
    if (location.pathname === "/signup/welcome") {
      return;
    }
    if (restoreToken(location)) {
      void tryLoadAppWithToken(); // if the user has a valid token, we try to load the app instead of sign up screen
    }
  }, [location.pathname, tryLoadAppWithToken]);

  const { t } = useTranslation();
  const [signupError, setSignupError] = useState<boolean>(false);
  const formik = useFormik<FormValues>({
    initialValues: {
      mail: params.get("mail") || storedEmail || "",
      password: "",
      confirmPassword: "",
    },
    onSubmit: () => {
      void handleSignup();
    },
    validate: (values) => {
      setSignupError(false);
      const errors: FormErrors = {};
      if (values.password !== values.confirmPassword) {
        errors.confirmPassword = t("signup.confirmPassword.error");
      }
      return errors;
    },
  });
  // even if we don't have the language supported yet,
  // we still want people to be able to save their prefered language.
  // That way we know which missing translations we need to add.
  const language = navigator.language;

  restoreToken(location); // for now trying to restore token from url only on signup

  const handleSignup = async () => {
    try {
      await createUser({
        guid: "new",
        farms: [],
        name: storedName,
        mail: formik.values.mail,
        phone: storedPhone,
        tradingName: storedTradingName || null,
        password: formik.values.password,
        lang: language,
      });

      analytics.track(AnalyticsEvents.SIGNUP_COMPLETED);
      analytics.track(AnalyticsEvents.INITIAL_LOGIN_WITH_EMAIL_STARTED);
      await fetchTokenAndStore({
        mail: formik.values.mail,
        password: formik.values.password,
      });
      analytics.track(AnalyticsEvents.INITIAL_LOGIN_COMPLETED);
      // Await initialisation so the user store is populated before we
      // navigate. Otherwise post-signup landings (e.g. /invite/:token) race
      // the load and misread the auth state.
      await initializeApp();

      // Setup device registration after successful signup
      if (shouldRegisterDevice()) {
        try {
          await setupPushNotificationsAndRegisterDevice();
          markDeviceAsRegistered();
        } catch (error) {
          console.error(
            "Failed to setup device registration after signup:",
            error
          );
        }
      }

      const pendingInviteToken =
        window.localStorage.getItem("pendingInviteToken");
      if (pendingInviteToken) {
        navigate(`/invite/${pendingInviteToken}`);
      } else if (params.get("from")) {
        navigate(`${params.get("from")}?from=signup`);
      } else {
        navigate("/signup/welcome");
      }
    } catch (err) {
      console.log(err);
      setSignupError(true);
      analytics.track(AnalyticsEvents.INITIAL_LOGIN_FAILED);
    }
  };

  return (
    <Box>
      <Routes>
        <Route
          path="/"
          element={<Navigate to="/signup/user-details" replace />}
        />
        <Route
          path="/welcome"
          element={
            <FullScreenDialog>
              <Welcome />
            </FullScreenDialog>
          }
        />
        <Route
          path="/user-details"
          element={
            <UserDetails
              values={formik.values}
              errors={formik.errors}
              handleChange={formik.handleChange}
              handleSubmit={formik.handleSubmit}
              signupError={signupError}
              loading={loading}
            />
          }
        />
      </Routes>
    </Box>
  );
}

export const StyledForm = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  margin: "auto",
  width: "250px",
  paddingBottom: theme.spacing(2),
}));
